import React, { createContext, useContext, useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { shouldUseMsal, loginRequest, getSharedMsalInstance } from '../config/msalConfig';
import authAxios from '../utils/authAxios';
import { isEmbedMode, onEmbedToken, getEmbedToken, notifyParentAuthed } from '../utils/embedAuth';

export const AuthContext = createContext();

// Fetch the server-synthesized profile from /api/me. The server is the source
// of truth for tenant tier, effective roles (post Graph guest resolution), and
// the permission set. Returns null on failure so callers can fall back to the
// locally-parsed Easy Auth / MSAL profile.
const fetchServerProfile = async () => {
  try {
    const res = await authAxios.get('/api/me');
    return res?.data || null;
  } catch (err) {
    console.warn('[AUTH] /api/me fetch failed:', err?.response?.status || err?.message);
    return null;
  }
};

const mergeServerProfile = (local, server) => {
  if (!server) return local;
  return {
    ...local,
    id: server.id || local?.id,
    tid: server.tid || local?.tid || null,
    name: server.name || local?.name || '',
    email: server.email || local?.email || '',
    tier: server.tier || null,
    isAdmin: !!server.isAdmin,
    isViewer: Array.isArray(server.roles)
      ? server.roles.some(r => String(r).toLowerCase() === 'viewer')
      : !!local?.isViewer,
    roles: Array.isArray(server.roles) ? server.roles : (local?.roles || []),
    permissions: Array.isArray(server.permissions) ? server.permissions : (local?.permissions || []),
    photoUrl: local?.photoUrl || '',
  };
};

// MSAL instance will be initialized in AuthProvider useEffect
// We'll use the shared instance from msalConfig to ensure consistency
let msalInstance = null;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState(null); // 'easyauth' | 'msal' | null
  const msalInitialized = useRef(false);

  // Parse claims from Easy Auth format (user_claims array)
  const parseEasyAuthClaims = (raw) => {
    if (!raw?.user_claims) throw new Error('No user_claims found');

    const getClaim = (type) =>
      raw.user_claims.find((c) => c.typ === type)?.val || '';

    const name = getClaim('name') || getClaim('preferred_username') || raw.user_id;
    const email = getClaim('preferred_username') || raw.user_id;

    const roles = raw.user_claims
      .filter(c => {
        const t = String(c.typ || c.type || '').toLowerCase();
        return t === 'roles' || t === 'role' || t.endsWith('/claims/role') || t.includes('identity/claims/role');
      })
      .map(c => c.val);

    const photoUrl = (() => {
      try {
        const pic = raw.user_claims.find(c => /picture|avatar/i.test(String(c.typ)))?.val;
        return pic || '';
      } catch { return ''; }
    })();

    return {
      name,
      email,
      roles,
      isAdmin: roles.some(r => String(r).toLowerCase() === 'admin'),
      isViewer: roles.some(r => String(r).toLowerCase() === 'viewer'),
      photoUrl,
    };
  };

  // Parse claims from MSAL account and ID token
  const parseMsalClaims = (account, idToken) => {
    // MSAL account object has basic info
    let name = account.name || '';
    let email = account.username || account.idTokenClaims?.email || account.idTokenClaims?.preferred_username || '';

    // Extract additional info from ID token claims if available
    let roles = [];
    try {
      // Try to get claims from ID token first
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        
        // Get name from token if not in account
        if (!name && (payload.name || payload.given_name)) {
          name = payload.name || `${payload.given_name || ''} ${payload.family_name || ''}`.trim();
        }
        
        // Get email from token if not in account
        if (!email) {
          email = payload.email || payload.preferred_username || payload.upn || '';
        }
        
        // Extract roles from ID token claims
        // Roles might be in different claim formats
        roles = payload.roles || 
                payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
                payload['http://schemas.microsoft.com/identity/claims/role'] ||
                [];
        if (typeof roles === 'string') roles = [roles];
      } else if (account.idTokenClaims) {
        // Fallback to idTokenClaims if ID token string not available
        const claims = account.idTokenClaims;
        if (!name && (claims.name || claims.given_name)) {
          name = claims.name || `${claims.given_name || ''} ${claims.family_name || ''}`.trim();
        }
        if (!email) {
          email = claims.email || claims.preferred_username || claims.upn || '';
        }
        // Extract roles from claims object
        roles = claims.roles || [];
        if (typeof roles === 'string') roles = [roles];
      }
    } catch (e) {
      console.warn('[AUTH] Could not parse claims from ID token:', e);
    }

    // Final fallback to account info
    if (!name && account.name) name = account.name;
    if (!email && account.username) email = account.username;

    return {
      name: name || email,
      email,
      roles: Array.isArray(roles) ? roles : [],
      isAdmin: roles.some(r => String(r).toLowerCase() === 'admin'),
      isViewer: roles.some(r => String(r).toLowerCase() === 'viewer'),
      photoUrl: account.idTokenClaims?.picture || '', // Photo URL from ID token if available
    };
  };

  // Load user from Easy Auth
  const loadEasyAuthUser = useCallback(async () => {
    try {
      const res = await fetch('/.auth/me', { credentials: 'include' });
      
      // Check if response is OK and is JSON
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      // Check content type to ensure it's JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON (Easy Auth not available)');
      }
      
      const data = await res.json();
      console.log('[AUTH] Easy Auth /.auth/me response:', data);

      // Accept SWA array shape, App Service object, or nested clientPrincipal
      const raw = Array.isArray(data) ? data[0] : (data?.clientPrincipal || data);
      if (!raw || !raw.user_id) {
        throw new Error('No principal found in response');
      }

      const profile = parseEasyAuthClaims(raw);
      console.log('[AUTH] ✓ Easy Auth user loaded:', {
        name: profile.name,
        email: profile.email,
        roles: profile.roles,
        isAdmin: profile.isAdmin,
        isViewer: profile.isViewer,
      });

      setUser(profile);
      setToken(raw.id_token || raw.access_token || null);
      setAuthMethod('easyauth');

      // Upgrade to the server-synthesized profile (tenant tier + effective roles
      // after Graph guest resolution). If /api/me fails, keep the local profile.
      const server = await fetchServerProfile();
      if (server) {
        setUser(prev => mergeServerProfile(prev || profile, server));
        console.log('[AUTH] ✓ Server profile applied', {
          tier: server.tier,
          roles: server.roles,
          isAdmin: server.isAdmin,
        });
      }
      return true;
    } catch (err) {
      // Silently fail if Easy Auth is not available (expected in local dev)
      // Only log if it's not a JSON parse error or expected error
      if (err.message && !err.message.includes('not valid JSON') && !err.message.includes('not JSON')) {
        console.log('[AUTH] Easy Auth not available:', err.message);
      }
      return false;
    }
  }, []);

  // Load user from MSAL
  const loadMsalUser = useCallback(async () => {
    console.log('[AUTH] Loading user from MSAL...');
    
    // Get shared MSAL instance if not already set
    if (!msalInstance) {
      console.log('[AUTH] Getting shared MSAL instance...');
      const sharedInstance = await getSharedMsalInstance();
      if (sharedInstance) {
        msalInstance = sharedInstance;
        msalInitialized.current = true;
        console.log('[AUTH] ✓ MSAL instance obtained');
      } else {
        console.warn('[AUTH] ✗ Failed to get MSAL instance');
      }
    }
    
    if (!msalInstance || !msalInitialized.current) {
      console.warn('[AUTH] ✗ MSAL not configured or not initialized');
      return false;
    }

    try {
      // Check for existing account
      const accounts = msalInstance.getAllAccounts();
      console.log('[AUTH] MSAL accounts found:', accounts.length);
      if (accounts.length === 0) {
        console.log('[AUTH] No MSAL account found - user needs to login');
        return false;
      }

      const account = accounts[0]; // Use first account
      console.log('[AUTH] MSAL account found:', account.username);

      // Try to get ID token from account, or acquire one silently
      let idToken = account.idToken;
      
      if (!idToken) {
        // ID token might not be in account object, try to acquire token silently
        try {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: account,
          });
          idToken = tokenResponse.idToken || tokenResponse.accessToken;
          console.log('[AUTH] Acquired token silently for existing account');
        } catch (tokenError) {
          console.warn('[AUTH] Could not acquire token silently:', tokenError);
          // If we can't get a token, the user might need to login again
          // But we can still try to use account info if available
          if (account.idTokenClaims) {
            // Try to extract user info from cached claims
            const profile = parseMsalClaims(account, null); // Parse from account only
            if (profile.email) {
              setUser(profile);
              setToken(null); // No token available
              setAuthMethod('msal');
              return true;
            }
          }
          return false;
        }
      }

      // Use ID token for user profile info and API calls
      // The backend middleware can validate ID tokens
      const profile = parseMsalClaims(account, idToken);
      console.log('[AUTH] MSAL profile:', profile);

      setUser(profile);
      setToken(idToken); // Use ID token for API calls (backend will validate it)
      setAuthMethod('msal');

      // Upgrade to the server-synthesized profile (tenant tier + effective roles
      // after Graph guest resolution). If /api/me fails, keep the local profile.
      const server = await fetchServerProfile();
      if (server) {
        setUser(prev => mergeServerProfile(prev || profile, server));
        console.log('[AUTH] ✓ Server profile applied (MSAL)', {
          tier: server.tier,
          roles: server.roles,
          isAdmin: server.isAdmin,
        });
      }
      return true;
    } catch (err) {
      console.error('[AUTH] MSAL load failed:', err);
      return false;
    }
  }, []);

  // Initialize MSAL on mount if configured and handle redirect responses
  // This MUST run first to handle redirect responses before normal user loading
  useEffect(() => {
    // Embed mode: identity comes from the parent frame (Tango) via
    // postMessage, not Easy Auth/MSAL — both are redirect-based and break
    // inside an iframe, which is the whole reason for the token bridge.
    if (isEmbedMode()) {
      let cancelled = false;
      // onEmbedToken stays silent until the parent frame actually posts a
      // message, so `loading` holds true while we wait — otherwise
      // RequireAuth bounces to /unauthorized before the token arrives and
      // UnauthorizedPage tries an MSAL loginRedirect (redirect_in_iframe).
      // Safety valve: if the parent never speaks, give up after 15s so the
      // user sees the unauthorized page instead of an infinite spinner.
      const bailTimer = setTimeout(() => {
        if (cancelled) return;
        console.warn('[AUTH] Embed mode: no token from parent frame after 15s');
        setLoading(false);
      }, 15000);
      const unsubscribe = onEmbedToken(async (embedToken) => {
        if (cancelled) return;
        clearTimeout(bailTimer);
        if (!embedToken) {
          setUser(null);
          setToken(null);
          setAuthMethod(null);
          setLoading(false);
          return;
        }
        setToken(embedToken);
        setAuthMethod('embed');
        // No local claims to parse (it's an opaque bridge token from
        // Tango's perspective) — the server is the only source of profile
        // info here, same as the "upgrade" step in the other auth paths.
        const server = await fetchServerProfile();
        // Guard the race: if a logout or a newer token arrived while /api/me was
        // in flight, this profile is stale — dropping it stops a logged-out
        // session from being re-authenticated.
        if (cancelled || getEmbedToken() !== embedToken) return;
        setUser(server ? mergeServerProfile(null, server) : null);
        setLoading(false);
        // Auth resolved for this token — tell Tango to reveal the iframe (drops
        // its loader). Fires once; on a rejected token this reveals Vault's own
        // unauthorized page.
        notifyParentAuthed();
      });
      return () => {
        cancelled = true;
        clearTimeout(bailTimer);
        unsubscribe();
      };
    }

    const initializeAuth = async () => {
      // Handle MSAL redirect first if configured
      if (shouldUseMsal() && !msalInitialized.current) {
        try {
          // Get or create shared MSAL instance
          const sharedInstance = await getSharedMsalInstance();
          if (sharedInstance) {
            msalInstance = sharedInstance;
            msalInitialized.current = true;
            
            // Handle redirect response after login (must be called after initialize)
            const response = await msalInstance.handleRedirectPromise();
            
            if (response && response.account) {
              console.log('[AUTH] MSAL redirect response received:', response);
              // User just logged in via redirect, extract user info
              // handleRedirectPromise should have already added account to cache
              const idToken = response.idToken;
              const profile = parseMsalClaims(response.account, idToken);
              setUser(profile);
              setToken(idToken);
              setAuthMethod('msal');

              // Upgrade to server-synthesized profile after redirect login.
              const server = await fetchServerProfile();
              if (server) {
                setUser(prev => mergeServerProfile(prev || profile, server));
                console.log('[AUTH] ✓ Server profile applied (redirect)', {
                  tier: server.tier,
                  roles: server.roles,
                  isAdmin: server.isAdmin,
                });
              }
              setLoading(false); // Mark loading as complete since we have user from redirect
              console.log('[AUTH] User logged in via redirect, account in cache:', response.account.username);
              return; // Don't continue to loadUser if we got redirect response
            }
            
            // Check if account exists in cache (for page refresh after login)
            // MSAL should have cached the account from redirect
            const cachedAccounts = msalInstance.getAllAccounts();
            if (cachedAccounts.length > 0) {
              console.log('[AUTH] Found', cachedAccounts.length, 'account(s) in MSAL cache');
              // Will be handled by loadMsalUser below
            } else {
              console.log('[AUTH] No accounts in MSAL cache after redirect check');
            }
          }
        } catch (err) {
          console.error('[AUTH] Error during MSAL initialization or redirect handling:', err);
        }
      }
      
      // If no redirect response or MSAL not configured, continue with normal user loading
      // If MSAL is explicitly enabled, prioritize it
      if (shouldUseMsal()) {
        console.log('[AUTH] MSAL is enabled, attempting to load user...');
        const msalSuccess = await loadMsalUser();
        // If MSAL failed, fall back to Easy Auth
        if (!msalSuccess) {
          console.log('[AUTH] MSAL failed, falling back to Easy Auth...');
          await loadEasyAuthUser();
        }
      } else {
        // Otherwise, use Easy Auth (default for Azure App Service)
        console.log('[AUTH] Using Easy Auth (default)...');
        await loadEasyAuthUser();
      }
      
      setLoading(false);
    };
    
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - functions are stable useCallback hooks

  // Login function - works with both Easy Auth and MSAL.
  // returnTo: where to land after login. Never default to the current URL
  // while on /unauthorized — that made AAD's callback bring the freshly
  // signed-in user right back to the Access Denied page.
  const login = useCallback(async (returnTo) => {
    console.log('[AUTH] Login initiated...');

    // Embed mode: identity only ever comes from the parent frame's token
    // bridge. Both interactive paths below are redirect-based and MSAL hard
    // rejects redirects inside an iframe (redirect_in_iframe).
    if (isEmbedMode()) {
      throw new Error('Embedded session: sign in happens in the host portal, not here.');
    }

    // If authMethod is not set, detect it
    let method = authMethod;
    if (!method) {
      console.log('[AUTH] Auth method not set, detecting...');
      // If MSAL is configured, prioritize it (especially for local dev)
      // Easy Auth will be used in production Azure App Service
      if (shouldUseMsal()) {
        method = 'msal';
        console.log('[AUTH] Detected MSAL as auth method');
      } else {
        // Only try Easy Auth if MSAL is not configured
        try {
          const res = await fetch('/.auth/me', { credentials: 'include' });
          const contentType = res.headers.get('content-type');
          if (res.ok && contentType && contentType.includes('application/json')) {
            // Easy Auth is actually available
            method = 'easyauth';
            console.log('[AUTH] Detected Easy Auth as auth method');
          } else if (res.status === 401) {
            // Only the Easy Auth module answers 401 on /.auth/me — the app
            // itself never serves that path (local dev returns 404/HTML). So a
            // 401 means Easy Auth is present and the visitor is signed OUT —
            // exactly who the login redirect below is for. Before the
            // allow-unauthenticated flip this never ran (the front door
            // redirected anonymous browsers to AAD itself).
            method = 'easyauth';
            console.log('[AUTH] Detected Easy Auth (anonymous 401) as auth method');
          }
        } catch (err) {
          console.log('[AUTH] Easy Auth detection failed:', err.message);
        }
      }
    } else {
      console.log('[AUTH] Using existing auth method:', method);
    }

    if (method === 'easyauth') {
      // Easy Auth: redirect to platform login
      const dest = returnTo
        || (window.location.pathname === '/unauthorized' ? '/' : window.location.href);
      const redirectUri = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(dest);
      console.log('[AUTH] Redirecting to Easy Auth login:', redirectUri);
      window.location.href = redirectUri;
      return;
    }

    // MSAL: interactive login
    if (shouldUseMsal()) {
      console.log('[AUTH] Initiating MSAL login...');
      // Ensure MSAL is initialized - use shared instance
      if (!msalInstance) {
        try {
          const sharedInstance = await getSharedMsalInstance();
          if (sharedInstance) {
            msalInstance = sharedInstance;
            msalInitialized.current = true;
            console.log('[AUTH] MSAL initialized for login using shared instance');
          } else {
            throw new Error('Failed to get MSAL instance');
          }
        } catch (initErr) {
          console.error('[AUTH] Failed to initialize MSAL:', initErr);
          throw new Error('Failed to initialize MSAL. Check REACT_APP_USE_MSAL and REACT_APP_AZURE_CLIENT_ID configuration.');
        }
      } else if (!msalInitialized.current) {
        // Ensure it's initialized
        try {
          if (!msalInstance.getAllAccounts || typeof msalInstance.getAllAccounts !== 'function') {
            await msalInstance.initialize();
          }
          msalInitialized.current = true;
        } catch (initErr) {
          console.error('[AUTH] MSAL initialization failed:', initErr);
          throw new Error('MSAL initialization failed. Please check your configuration.');
        }
      }

      try {
        console.log('[AUTH] Triggering MSAL login redirect with scopes:', loginRequest.scopes);
        // Use redirect instead of popup - will navigate away and come back.
        // redirectStartPage: land on the caller's intended page instead of
        // the configured redirectUri (origin) after the redirect completes.
        await msalInstance.loginRedirect(
          returnTo ? { ...loginRequest, redirectStartPage: returnTo } : loginRequest
        );
        // Note: loginRedirect will navigate away, so code below won't execute
        // User will be redirected back after login, and loadMsalUser will be called on page load
      } catch (err) {
        console.error('[AUTH] MSAL login redirect failed:', err);
        // Don't throw if user cancelled
        if (err.errorCode === 'user_cancelled' || err.errorCode === 'consent_required') {
          throw new Error('Login was cancelled or consent is required.');
        }
        throw err;
      }
    } else if (!method) {
      throw new Error('No authentication method configured. Set REACT_APP_USE_MSAL=true and REACT_APP_AZURE_CLIENT_ID for MSAL, or enable Easy Auth in Azure.');
    }
  }, [authMethod]);

  // Logout function
  const logout = useCallback(async () => {
    // Detect auth method if not set
    let method = authMethod;
    if (!method) {
      try {
        const res = await fetch('/.auth/me', { credentials: 'include' });
        if (res.ok) {
          method = 'easyauth';
        }
      } catch {}
      if (!method && shouldUseMsal()) {
        method = 'msal';
      }
    }

    if (method === 'easyauth') {
      // Easy Auth: redirect to platform logout
      window.location.href = '/.auth/logout?post_logout_redirect_uri=' + encodeURIComponent(window.location.origin);
      return;
    }

    // MSAL: logout
    if (shouldUseMsal() && msalInstance) {
      try {
        // Clear local state first
        setUser(null);
        setToken(null);
        setAuthMethod(null);
        
        // Clear MSAL cache
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          // Use redirect logout for better UX (no popup)
          await msalInstance.logoutRedirect({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin,
          });
          // Note: logoutRedirect will navigate away, so code below won't execute
        } else {
          // No accounts, just clear cache
          await msalInstance.clearCache();
        }
      } catch (err) {
        console.error('[AUTH] MSAL logout failed:', err);
        // Clear local state even if logout fails
        setUser(null);
        setToken(null);
        setAuthMethod(null);
        
        // Try to clear cache anyway
        try {
          await msalInstance.clearCache();
        } catch (cacheErr) {
          console.error('[AUTH] Failed to clear MSAL cache:', cacheErr);
        }
        
        // Navigate to home page as fallback
        window.location.href = '/';
      }
    } else {
      // Fallback: clear local state
      setUser(null);
      setToken(null);
      setAuthMethod(null);
      
      // Navigate to home which will redirect to login
      window.location.href = '/';
    }
  }, [authMethod]);

  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    authMethod,
    login,
    logout,
  }), [user, token, loading, authMethod, login, logout]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
